import { Component, OnInit, OnChanges } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { getTenantStorage } from '../../../_shared/utils/tenant-context';

import { CrmCompareData, SingleCompany} from '../../../_shared/interfaces';
import {
  AlertService,
  ConditionStoreService,
  ContactsStoreService,
  CrmStoreService,
  IAlertType
} from '../../../_shared/services';

import { NgFor, NgIf } from "@angular/common";
import { ClrIconModule, ClrModalModule } from "@clr/angular";
import { ClarityIcons, twoWayArrowsIcon } from "@cds/core/icon";
import { ContactFilesCardComponent } from "../contact-files-card/contact-files-card.component";
import { PersonsCardComponent } from "../../persons/persons-card/persons-card.component";
import { ProjectCardComponent } from "../../projects/project-card/project-card.component";
import { RequestCardComponent } from "../../requests/request-card/request-card.component";
import { TranslocoDirective } from "@jsverse/transloco";

import { environment as env } from "../../../../environments/environment";
import { take } from "rxjs/operators";

@Component({
  selector: 'app-contact-details',
  standalone: true,
  imports: [
    RouterLink,
    NgIf,
    NgFor,
    ClrIconModule,
    ClrModalModule,
    TranslocoDirective,
    ContactFilesCardComponent,
    PersonsCardComponent,
    ProjectCardComponent,
    RequestCardComponent
  ],
  templateUrl: './contact-details.component.html',
  styleUrl: './contact-details.component.sass'
})

export class ContactDetailsComponent implements OnInit, OnChanges {
  devMode: boolean;
  crmMode: boolean;
  company: SingleCompany | null = null;
  companyId: number = 0;
  delivery: string = '';
  payment: string = '';
  public makeACall = false;
  crmCompare: CrmCompareData | null = null;
  crmCompareLoading = false;
  crmSyncRunning = false;

  constructor(
    private css: ContactsStoreService,
    private condss: ConditionStoreService,
    private crmss: CrmStoreService,
    private router: Router,
    private route: ActivatedRoute,
    private alertDispatcher: AlertService
  ) {
    this.devMode = env.stages.dev;
    this.crmMode = env.externalCrm;
    ClarityIcons.addIcons(twoWayArrowsIcon);
  }

  ngOnInit() {
    const params = this.route.snapshot.paramMap;
    this.companyId = parseInt(params.get('cid') ?? '0', 10);
    this.loadSingleContact(this.companyId);
  }

  ngOnChanges() { }

  loadSingleContact(contactID: number) {
    this.css.getSingleCompany(contactID)
      .subscribe({
        next: res => {
          if (!res || !res.object || res.object.length === 0) {
            this.alertDispatcher.alertConfig.message = 'Keine Firmendaten gefunden!';
            this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
            return;
          }
          this.company = res.object[0];
          this.getDeliveryCondition(this.company.cond_delivery);
          this.getPaymentCondition(this.company.cond_payment);
          if (this.crmMode) {
            this.loadCrmContact(this.company.crm_id);
            this.loadCrmCompare(this.company.cid);
          }
        },
        complete: () => {
        },
        error: () => {
          return
        }
      })
  }

  loadCrmContact(crmId: number | null | undefined){
    this.crmss.crmLoadContact(crmId);
  }

  get crmId(): string {
    const value = this.company?.crm_id;
    return value === null || value === undefined ? '' : value.toString();
  }

  get crmIdStatus(): string {
    if (!this.crmMode) return '';
    return this.crmId ? 'OK' : 'Fehlt';
  }

  loadCrmCompare(contactId: number) {
    const sessionId = this.crmss.getCrmToken() ?? '';
    this.crmCompareLoading = true;
    this.css.getCrmCompare(contactId, sessionId).subscribe({
      next: (res) => {
        this.crmCompare = res?.data ?? null;
      },
      error: () => {
        this.crmCompare = null;
      },
      complete: () => {
        this.crmCompareLoading = false;
      }
    });
  }

  getDiffFieldLabel(field: string): string {
    const labels: Record<string, string> = {
      company: 'Firma',
      street: 'Straße',
      postcode: 'PLZ',
      city: 'Stadt',
      email: 'E-Mail',
      phone: 'Telefon'
    };
    return labels[field] ?? field;
  }

  get crmCompareLabel(): string {
    if (!this.crmMode) return 'CRM aus';
    if (this.crmCompareLoading) return 'Pruefung laeuft';
    if (!this.crmCompare) return 'Nicht verfuegbar';
    return this.crmCompare.in_sync ? 'Synchron' : 'Abweichung';
  }

  get crmCompareClass(): string {
    if (!this.crmMode) return 'label';
    if (this.crmCompareLoading) return 'label label-info';
    if (!this.crmCompare) return 'label label-warning';
    return this.crmCompare.in_sync ? 'label label-success' : 'label label-danger';
  }

  syncCrmNow() {
    if (!this.company || this.crmSyncRunning) return;
    const staffId = parseInt(getTenantStorage('staff_id') ?? getTenantStorage('user_id') ?? '0', 10);

    if (!staffId) {
      this.alertDispatcher.show({
        message: 'CRM-Sync nicht möglich: Staff-ID fehlt.',
        clrAlertType: IAlertType.WARNING
      }, 4000);
      return;
    }

    this.crmss.crmProviderName().pipe(take(1)).subscribe(provider => {
      const sessionId = this.crmss.getCrmToken() ?? '';

      if (provider === 'tecart' && !sessionId) {
        this.alertDispatcher.show({
          message: 'CRM-Sync nicht möglich: CRM-Token fehlt.',
          clrAlertType: IAlertType.WARNING
        }, 4000);
        return;
      }

      this.crmSyncRunning = true;
      this.css.syncCrmContact(this.company!.cid, {
        session_id: sessionId,
        staff_id: staffId
      }).subscribe({
        next: (res) => {
          this.crmCompare = res?.data ?? this.crmCompare;
          this.alertDispatcher.show({
            message: this.crmCompare?.in_sync ? 'CRM-Sync erfolgreich.' : 'CRM-Sync mit Abweichungen abgeschlossen.',
            clrAlertType: this.crmCompare?.in_sync ? IAlertType.SUCCESS : IAlertType.WARNING
          }, 3000);
        },
        error: () => {
          this.alertDispatcher.show({
            message: 'CRM-Sync fehlgeschlagen.',
            clrAlertType: IAlertType.DANGER
          }, 4000);
        },
        complete: () => {
          this.crmSyncRunning = false;
        }
      });
    });
  }

  getDeliveryCondition(condid: number) {
    this.condss.getConditionById(condid, 2)
      .subscribe({
        next: condition => {
          if (!condition?.object) return;
            this.delivery = condition.object[0].name;
          },
        error: () => {
          return
        }
      })
  }

  getPaymentCondition(condid: number) {
    this.condss.getConditionById(condid, 1)
      .subscribe({
        next: condition => {
          if (!condition?.object) return;
            this.payment = condition.object[0].name;
          },
        error: () => {
            return
          }
      })
  }

  deleteCompany(cid: number, cname: string) {
    if (confirm('Firma ' + cname + ' wirklich löschen?\nDie Firma wird auch im TecArt gelöscht!\nDiese Aktion kann nicht rückgängig gemacht werden!')) {
      this.css.deleteCompany(cid)
        .subscribe({
          next: () => {
            this.crmss.crmDeleteContact(this.company?.crm_id)
          },
          complete: () => {
            this.router.navigate(['/contacts/overview'], {relativeTo: this.route})
          },
          error: () => {
            return
          }
        });
    }
  }

  callUrl(phoneNumber: string) {
    const normalizedPhoneNumber = phoneNumber.replace(/[^\d+]/g, '');
    return `tel:${normalizedPhoneNumber}`;
  }

  startCall(phoneNumber: string) {
    window.location.href = this.callUrl(phoneNumber);
  }
}
